import * as Axios from 'axios';
import BaseService from '../common/BaseService';
import store from '@/store';
import {
    IBrokerGroup,
    IBrokerGroupSummary,
    IBroker,
    IPanelLender,
    IBrokerAudit,
    BrokerGroupStatus
} from '@/domain/bankwatch';

// Client for the pc-admin-server broker-group admin API (Stories 1.3 / 1.4),
// mounted under /bankwatch/broker-groups.
export default class BrokerGroupService extends BaseService {

    private base() {
        return `${store.state.apiBase}/bankwatch/broker-groups`;
    }

    private authHeader() {
        return { Authorization: `Bearer ${store.state.$auth.token}` };
    }

    // ---- Groups (1.3) ----

    public async getGroups(): Promise<IBrokerGroupSummary[]> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'get', url: `${this.base()}/`, headers: this.authHeader()
        });
        return res.data as IBrokerGroupSummary[];
    }

    public async createGroup(name: string, status: BrokerGroupStatus): Promise<IBrokerGroup> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'post', url: `${this.base()}/`, data: { name, status }, headers: this.authHeader()
        });
        return res.data as IBrokerGroup;
    }

    public async updateGroup(groupId: string, updates: { name?: string; status?: BrokerGroupStatus }): Promise<IBrokerGroup> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'put', url: `${this.base()}/${groupId}`, data: updates, headers: this.authHeader()
        });
        return res.data as IBrokerGroup;
    }

    // ---- Brokers (1.3) ----

    public async getBrokers(groupId: string): Promise<IBroker[]> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'get', url: `${this.base()}/${groupId}/brokers`, headers: this.authHeader()
        });
        return res.data as IBroker[];
    }

    public async addBroker(groupId: string, email: string, firstName?: string, lastName?: string): Promise<IBroker> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'post', url: `${this.base()}/${groupId}/brokers`,
            data: { email, firstName, lastName }, headers: this.authHeader()
        });
        return res.data as IBroker;
    }

    public async setBrokerAccess(groupId: string, brokerId: string, enabled: boolean): Promise<IBroker> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'put', url: `${this.base()}/${groupId}/brokers/${brokerId}/access`,
            data: { enabled }, headers: this.authHeader()
        });
        return res.data as IBroker;
    }

    // ---- Lender panel (1.4) ----

    public async getLenders(): Promise<IPanelLender[]> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'get', url: `${this.base()}/lenders`, headers: this.authHeader()
        });
        return res.data as IPanelLender[];
    }

    public async getPanel(groupId: string): Promise<{ groupId: string; panelOrgIds: string[] }> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'get', url: `${this.base()}/${groupId}/panel`, headers: this.authHeader()
        });
        return res.data;
    }

    public async setPanel(groupId: string, orgIds: string[]): Promise<{ groupId: string; panelOrgIds: string[] }> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'put', url: `${this.base()}/${groupId}/panel`, data: { orgIds }, headers: this.authHeader()
        });
        return res.data;
    }

    // ---- Audit log (1.3 ac4) ----

    public async getGroupAudit(groupId: string): Promise<IBrokerAudit[]> {
        const res: Axios.AxiosResponse = await this.makeAxiosCall({
            method: 'get', url: `${this.base()}/${groupId}/audit`, headers: this.authHeader()
        });
        return res.data as IBrokerAudit[];
    }
}
